import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logo from "@/assets/logo.png";
import { REDIRECT_AFTER_LOGIN_KEY } from "@/components/InactivityWatcher";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Connexion réussie");
      const redirect = localStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
      if (redirect) {
        localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
        navigate(redirect);
      } else {
        navigate("/app");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background grid-pattern px-4">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
      >
        <div className="px-8 pb-6 pt-8 text-center">
          <Link to="/" className="mx-auto flex w-40 items-center justify-center">
            <motion.img
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              src={logo}
              alt="École de Commerce de Lyon — Campus Abidjan"
              className="w-full"
            />
          </Link>
        </div>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.35, ease: "easeInOut" }}
          className="flag-stripe origin-left"
          aria-hidden="true"
        >
          <span /><span /><span />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="px-8 pb-8 pt-6"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">Campus Abidjan</p>
          <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight">Connexion</h1>
          <p className="mt-1 text-sm text-muted-foreground">Accédez à votre espace pédagogique.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="username">Identifiant</Label>
              <Input id="username" type="text" autoCapitalize="none" autoCorrect="off" value={username} onChange={(e) => setUsername(e.target.value)} required data-testid="login-username" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="login-submit">
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Les comptes sont créés par l'administration. Contactez votre administrateur.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
